import React, { useState, useEffect, useCallback } from 'react';
import TiposYPartesConfig from './configuracion/TiposYPartesConfig';
import CuentaConfig from './configuracion/CuentaConfig';
import ClientesConfig from './configuracion/ClientesConfig';
import PersonalConfig from './configuracion/PersonalConfig';
import AprobacionesConfig from './configuracion/AprobacionesConfig';
import BackupConfig from './configuracion/BackupConfig';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';

const Configuracion = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // Establecer pestaña inicial según el rol
  const getInitialTab = useCallback(() => {
    if (user && (user.rol === 'JEFE' || user.rol === 'ADMIN')) {
      return 'tipos';
    }
    return 'cuenta';
  }, [user]);
  
  const [activeTab, setActiveTab] = useState(getInitialTab());

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  // SVG Icons como componentes
  const TabIcon = ({ type }) => {
    const icons = {
      tipos: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      ),
      clientes: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      personal: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
      aprobaciones: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      ),
      backup: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      ),
      cuenta: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      )
    };
    return icons[type] || null;
  };

  // Definir pestañas según el rol del usuario
  const getTabs = () => {
    const tabs = [];

    // ADMIN: Solo puede gestionar tipos de expediente y clientes
    if (user && user.rol === 'ADMIN') {
      tabs.push(
        { id: 'tipos', label: 'Tipos y Partes', color: '#4F46E5' },
        { id: 'clientes', label: 'Gestión de Clientes', color: '#059669' },
        { id: 'backup', label: 'Descarga de Datos', color: '#DC2626' }
      );
    }

    // JEFE: Puede gestionar personal y aprobaciones (además de lo que puede ADMIN)
    if (user && user.rol === 'JEFE') {
      tabs.push(
        { id: 'tipos', label: 'Tipos y Partes', color: '#4F46E5' },
        { id: 'clientes', label: 'Gestión de Clientes', color: '#059669' },
        { id: 'personal', label: 'Gestión de Personal', color: '#0891B2' },
        { id: 'aprobaciones', label: 'Aprobaciones', color: '#DC2626' },
        { id: 'backup', label: 'Descarga de Datos', color: '#EA580C' }
      );
    }

    // Todos los usuarios pueden ver Mi Cuenta
    tabs.push({ id: 'cuenta', label: 'Mi Cuenta', color: '#7C3AED' });

    return tabs;
  };

  const tabs = getTabs();

  // Actualizar pestaña activa cuando cambie el usuario o cuando la pestaña actual no esté disponible
  useEffect(() => {
    const availableTabIds = tabs.map(tab => tab.id);
    if (!availableTabIds.includes(activeTab)) {
      setActiveTab(getInitialTab());
    }
  }, [user, tabs, activeTab, getInitialTab]);

  return (
    <div className="container" style={{ padding: isMobile ? '10px' : '20px', maxWidth: isMobile ? '100%' : '1200px' }}>
      <div className="card" style={{ 
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', 
        border: '1px solid #E5E7EB', 
        borderRadius: '16px',
        overflow: 'hidden'
      }}>
        <div className="card-header" style={{ 
          background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          color: 'white',
          border: 'none',
          padding: '32px 40px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.875rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                Configuración del Sistema
              </h2>
              <p style={{ margin: '4px 0 0 0', opacity: 0.8, fontSize: '0.95rem', fontWeight: '400' }}>
                {user?.rol === 'PERSONAL' 
                  ? 'Gestiona tu cuenta y configuraciones personales'
                  : 'Administra los tipos de expedientes, clientes y configuraciones'
                }
              </p>
            </div>
          </div>
        </div>
        <div className="card-body" style={{ padding: '0', background: '#F9FAFB' }}>
          {/* Navegación por pestañas */}
          <div style={{ 
            padding: isMobile ? '16px' : '24px 40px',
            background: 'white',
            borderBottom: '1px solid #E5E7EB'
          }}>
            <div className="nav nav-tabs" style={{ 
              border: 'none', 
              gap: isMobile ? '8px' : '12px',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: isMobile ? 'flex-start' : 'flex-start',
              flexWrap: 'wrap'
            }}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    border: activeTab === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',
                    borderRadius: '12px',
                    padding: isMobile ? '12px 16px' : '14px 24px',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: activeTab === tab.id ? `${tab.color}08` : 'transparent',
                    color: activeTab === tab.id ? tab.color : '#6B7280',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    fontSize: '0.9375rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: isMobile ? '100%' : 'auto',
                    textAlign: 'left',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = '#F3F4F6';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.id) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    opacity: activeTab === tab.id ? 1 : 0.7
                  }}>
                    <TabIcon type={tab.id} />
                  </span>
                  <span>{tab.label}</span>
                  {activeTab === tab.id && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: tab.color,
                      borderRadius: '3px 3px 0 0'
                    }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Contenido */}
          <div style={{ 
            minHeight: '500px', 
            padding: isMobile ? '20px' : '32px 40px',
            background: '#F9FAFB'
          }}>
            {activeTab === 'tipos' && <TiposYPartesConfig />}
            {activeTab === 'clientes' && <ClientesConfig />}
            {activeTab === 'personal' && <PersonalConfig />}
            {activeTab === 'aprobaciones' && <AprobacionesConfig />}
            {activeTab === 'backup' && <BackupConfig />}
            {activeTab === 'cuenta' && <CuentaConfig />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuracion;


